import React, { useState, useEffect } from 'react';

const PracticeQuiz = ({ lessonId, lessonTitle, subject, onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiMarkingEnabled, setAiMarkingEnabled] = useState(true);
  const [manualMarks, setManualMarks] = useState({});
  const [markingFeedback, setMarkingFeedback] = useState({});

  // Pre-made quiz data for different lessons
  const quizData = {
    'physics-2-5': {
      title: 'Friction Practice Quiz',
      description: 'Test your understanding of friction forces and their effects',
      questions: [
        {
          id: 1,
          type: 'multiple-choice',
          question: 'Friction always acts in which direction relative to motion?',
          options: [
            'In the same direction as motion',
            'Opposite to the direction of motion',
            'Perpendicular to the direction of motion',
            'At a 45-degree angle to motion'
          ],
          correct: 1,
          explanation: 'Friction always opposes motion, acting in the opposite direction to slow down or prevent movement.',
          points: 2
        },
        {
          id: 2,
          type: 'multiple-choice',
          question: 'Which of these is an advantage of friction?',
          options: [
            'It wastes energy as heat',
            'It limits maximum speed',
            'It allows us to grip objects',
            'It wears down surfaces'
          ],
          correct: 2,
          explanation: 'Friction between our hands and objects allows us to grip and hold things securely.',
          points: 2
        },
        {
          id: 3,
          type: 'short-answer',
          question: 'Name two disadvantages of friction and explain how each affects everyday life.',
          sampleAnswer: 'Energy waste: Friction converts useful energy into heat (like in car brakes). Speed limitation: Air resistance prevents vehicles from reaching higher speeds.',
          points: 4,
          markingCriteria: [
            { point: 'Mentions energy waste/heat generation', marks: 1 },
            { point: 'Explains energy waste example', marks: 1 },
            { point: 'Mentions speed limitation/air resistance', marks: 1 },
            { point: 'Explains speed limitation example', marks: 1 }
          ]
        },
        {
          id: 4,
          type: 'checkbox',
          question: 'Which forces are shown in a typical friction diagram? (Select all that apply)',
          options: [
            'Weight (downward)',
            'Normal force (upward)',
            'Applied force (horizontal)',
            'Friction force (opposite to motion)',
            'Centripetal force'
          ],
          correct: [0, 1, 2, 3],
          explanation: 'A friction diagram typically shows weight, normal force, applied force, and friction force. Centripetal force is not typically shown in basic friction diagrams.',
          points: 4
        }
      ]
    }
  };

  const quiz = quizData[lessonId];

  useEffect(() => {
    if (!quiz || !lessonId) return;

    // Initialize answers object only once when lesson changes
    const initialAnswers = {};
    quiz.questions.forEach(q => {
      if (q.type === 'checkbox') {
        initialAnswers[q.id] = [];
      } else {
        initialAnswers[q.id] = '';
      }
    });
    console.log('Initializing answers:', initialAnswers); // Debug log
    setAnswers(initialAnswers);
  }, [lessonId]); // Only depend on lessonId, not the entire quiz object

  const handleAnswerChange = (questionId, answer) => {
    console.log('Answer changing:', { questionId, answer }); // Debug log
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleCheckboxChange = (questionId, optionIndex, checked) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: checked
        ? [...(prev[questionId] || []), optionIndex]
        : (prev[questionId] || []).filter(i => i !== optionIndex)
    }));
  };

  const generateAIFeedback = async (question, userAnswer, isCorrect) => {
    if (!aiMarkingEnabled) return null;

    // For now, return pre-written contextual feedback based on the question and correctness
    // This can be enhanced later with actual AI integration
    if (question.type === 'multiple-choice') {
      if (isCorrect) {
        return "Excellent! You've correctly identified the key concept. This shows you understand how friction works in physical systems.";
      } else {
        return "Not quite right. Think about the fundamental principle: friction always opposes motion. Review the lesson content about force directions.";
      }
    } else if (question.type === 'checkbox') {
      if (isCorrect) {
        return "Great job! You've identified all the correct forces that appear in friction diagrams. This shows good understanding of force analysis.";
      } else {
        return "You're on the right track, but you might have missed some forces or included extras. Remember that typical friction diagrams show weight, normal force, applied force, and friction force.";
      }
    } else if (question.type === 'short-answer') {
      if (!userAnswer || userAnswer.trim() === '') {
        return "You don't seem to have submitted an answer. Please try again, or use the points above to mark it yourself.";
      } else {
        return "Good effort! For questions about friction disadvantages, try to think about: 1) Energy conversion (useful energy → heat), 2) Speed limitations (air resistance), and 3) Wear and tear on surfaces. Compare your answer with the sample answer provided.";
      }
    }

    return "Keep working on understanding the concepts. Review the lesson material if you need more help.";
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let earnedPoints = 0;

    quiz.questions.forEach(question => {
      totalPoints += question.points;
      const userAnswer = answers[question.id];

      if (question.type === 'multiple-choice') {
        if (userAnswer == question.correct) {
          earnedPoints += question.points;
        }
      } else if (question.type === 'checkbox') {
        const correctCount = question.correct.filter(correct =>
          userAnswer && userAnswer.includes(correct)
        ).length;
        const incorrectCount = userAnswer ? userAnswer.filter(selected =>
          !question.correct.includes(selected)
        ).length : 0;

        const partialScore = Math.max(0, (correctCount - incorrectCount) / question.correct.length);
        earnedPoints += Math.round(partialScore * question.points);
      }
      // Short answer questions will be manually marked or use AI assistance
    });

    return { earnedPoints, totalPoints };
  };

  const handleSubmit = async () => {
    setLoading(true);
    const newFeedback = {};

    // Generate AI feedback for each question
    for (const question of quiz.questions) {
      const userAnswer = answers[question.id];
      let isCorrect = false;

      if (question.type === 'multiple-choice') {
        isCorrect = userAnswer == question.correct;
      } else if (question.type === 'checkbox') {
        const userSet = new Set(userAnswer || []);
        const correctSet = new Set(question.correct);
        isCorrect = userSet.size === correctSet.size &&
                   [...userSet].every(x => correctSet.has(x));
      }

      const aiFeedback = await generateAIFeedback(question, userAnswer, isCorrect);

      newFeedback[question.id] = {
        isCorrect,
        aiFeedback,
        explanation: question.explanation
      };
    }

    setFeedback(newFeedback);
    setShowResults(true);
    setLoading(false);
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setShowResults(false);
    setFeedback({});
    setManualMarks({});
    setMarkingFeedback({});

    // Reinitialize answers
    const initialAnswers = {};
    if (quiz && quiz.questions) {
      quiz.questions.forEach(q => {
        if (q.type === 'checkbox') {
          initialAnswers[q.id] = [];
        } else {
          initialAnswers[q.id] = '';
        }
      });
    }
    setAnswers(initialAnswers);
  };

  if (!quiz) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Practice Quiz Not Available</h3>
            <p className="text-gray-600 mb-4">
              No practice quiz is available for this lesson yet. We're working on creating more content!
            </p>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const { earnedPoints, totalPoints } = calculateScore();
    const percentage = Math.round((earnedPoints / totalPoints) * 100);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[9999] p-4 overflow-y-auto">
        <div className="bg-white rounded-lg max-w-5xl w-full my-4 shadow-2xl"
             style={{ maxHeight: 'calc(100vh - 2rem)' }}>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Quiz Results</h2>
                <p className="text-gray-600">{quiz.title}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{percentage}%</div>
                <div className="text-sm text-gray-500">{earnedPoints} / {totalPoints} points</div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
            {quiz.questions.map((question, index) => {
              const userAnswer = answers[question.id];
              const questionFeedback = feedback[question.id];

              return (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Question {index + 1} ({question.points} points)
                    </h3>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      questionFeedback?.isCorrect
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {questionFeedback?.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{question.question}</p>

                  {question.type === 'multiple-choice' && (
                    <div className="space-y-2 mb-4">
                      {question.options.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          className={`p-2 rounded border ${
                            optIndex == question.correct
                              ? 'border-green-500 bg-green-50'
                              : optIndex == userAnswer && optIndex != question.correct
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200'
                          }`}
                        >
                          <span className="font-medium">
                            {String.fromCharCode(65 + optIndex)}.
                          </span> {option}
                          {optIndex == userAnswer && (
                            <span className="ml-2 text-sm text-blue-600">← Your answer</span>
                          )}
                          {optIndex == question.correct && (
                            <span className="ml-2 text-sm text-green-600">← Correct answer</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {question.type === 'checkbox' && (
                    <div className="space-y-2 mb-4">
                      {question.options.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          className={`p-2 rounded border flex items-center ${
                            question.correct.includes(optIndex)
                              ? 'border-green-500 bg-green-50'
                              : userAnswer && userAnswer.includes(optIndex)
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={userAnswer && userAnswer.includes(optIndex)}
                            disabled
                            className="mr-2"
                          />
                          {option}
                          {userAnswer && userAnswer.includes(optIndex) && (
                            <span className="ml-2 text-sm text-blue-600">← Your choice</span>
                          )}
                          {question.correct.includes(optIndex) && (
                            <span className="ml-2 text-sm text-green-600">← Should be selected</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {question.type === 'short-answer' && (
                    <div className="space-y-3 mb-4">
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Your answer:</div>
                        <div className="p-3 bg-gray-50 rounded border">
                          {userAnswer || <span className="text-gray-400 italic">No answer provided</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Sample answer:</div>
                        <div className="p-3 bg-green-50 rounded border border-green-200">
                          {question.sampleAnswer}
                        </div>
                      </div>
                      {question.markingCriteria && (
                        <div className="border-l-4 border-blue-400 pl-4">
                          <div className="text-sm font-medium text-gray-700 mb-3">
                            Tick off the marks that you got:
                          </div>
                          <div className="space-y-3">
                            {question.markingCriteria.map((criteria, critIndex) => (
                              <div key={critIndex} className="bg-gray-50 p-3 rounded">
                                <label className="flex items-start space-x-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="mt-1 text-blue-600 rounded focus:ring-blue-500"
                                    checked={manualMarks[`${question.id}-${critIndex}`] || false}
                                    onChange={(e) => {
                                      setManualMarks(prev => ({
                                        ...prev,
                                        [`${question.id}-${critIndex}`]: e.target.checked
                                      }));
                                    }}
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{criteria.point}</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      {criteria.marks} mark{criteria.marks !== 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </label>
                              </div>
                            ))}
                          </div>

                          <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="text-sm font-medium text-blue-900 mb-1">Our feedback</div>
                            <div className="text-blue-800 text-sm">
                              {userAnswer.trim() === ''
                                ? "You don't seem to have submitted an answer. Please try again, or use the points above to mark it yourself."
                                : questionFeedback?.aiFeedback || "We're analyzing your answer..."
                              }
                            </div>
                          </div>

                          <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  // Reset manual marks for this question
                                  const newMarks = { ...manualMarks };
                                  question.markingCriteria.forEach((_, critIndex) => {
                                    delete newMarks[`${question.id}-${critIndex}`];
                                  });
                                  setManualMarks(newMarks);
                                }}
                                className="text-sm text-gray-600 hover:text-gray-800 underline"
                              >
                                Reset
                              </button>
                            </div>

                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {Object.keys(manualMarks).filter(key =>
                                  key.startsWith(`${question.id}-`) && manualMarks[key]
                                ).reduce((total, key) => {
                                  const critIndex = parseInt(key.split('-')[1]);
                                  return total + (question.markingCriteria[critIndex]?.marks || 0);
                                }, 0)} / {question.points}
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 text-xs text-gray-500">
                            We're still improving this feature. You can override the marks yourself by clicking them, or disable AI marking via your account settings.
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-sm font-medium text-blue-900 mb-1">Explanation:</div>
                      <div className="text-blue-800">{question.explanation}</div>
                    </div>

                    {questionFeedback?.aiFeedback && (
                      <div className="p-3 bg-purple-50 rounded border border-purple-200">
                        <div className="text-sm font-medium text-purple-900 mb-1">🤖 AI Feedback:</div>
                        <div className="text-purple-800">{questionFeedback.aiFeedback}</div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Did we mark this correctly?
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setMarkingFeedback(prev => ({
                            ...prev,
                            [question.id]: 'positive'
                          }))}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            markingFeedback[question.id] === 'positive'
                              ? 'bg-green-100 text-green-800 border border-green-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
                          }`}
                        >
                          👍 Yes
                        </button>
                        <button
                          onClick={() => setMarkingFeedback(prev => ({
                            ...prev,
                            [question.id]: 'negative'
                          }))}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            markingFeedback[question.id] === 'negative'
                              ? 'bg-red-100 text-red-800 border border-red-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
                          }`}
                        >
                          👎 No
                        </button>
                        <button
                          onClick={() => {
                            const feedback = prompt('Please tell us how we can improve our marking:');
                            if (feedback) {
                              console.log(`Marking feedback for question ${question.id}:`, feedback);
                              alert('Thank you for your feedback! We\'ll use this to improve our marking.');
                            }
                          }}
                          className="px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-sm"
                        >
                          💬 Feedback
                        </button>
                      </div>
                    </div>

                    {markingFeedback[question.id] && (
                      <div className="mt-2 text-sm text-gray-600">
                        {markingFeedback[question.id] === 'positive'
                          ? '✅ Thanks for confirming our marking was accurate!'
                          : '❌ Thank you for letting us know. We\'ll review this question and improve our marking algorithm.'
                        }
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sticky bottom-0 p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                >
                  🔄 Reset Quiz
                </button>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={aiMarkingEnabled}
                    onChange={(e) => setAiMarkingEnabled(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">Enable AI feedback</span>
                </label>
              </div>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg"
              >
                Close Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const isLastQuestion = currentQuestion === quiz.questions.length - 1;
  const userAnswer = answers[question.id];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🧠 {quiz.title}</h2>
              <p className="text-gray-600">{quiz.description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 min-h-[500px]">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                Question {currentQuestion + 1} of {quiz.questions.length}
              </div>
              <div className="text-sm font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                {question.points} point{question.points !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 leading-relaxed">{question.question}</h3>

            {question.type === 'multiple-choice' && (
              <div className="space-y-4">
                {question.options.map((option, index) => (
                  <label key={index} className="flex items-start space-x-4 p-4 border-2 border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all">
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={index}
                      checked={userAnswer == index}
                      onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
                      className="mt-1 text-blue-600 scale-125"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="font-bold text-gray-700 bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center text-sm">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-gray-900 text-lg">{option}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {question.type === 'checkbox' && (
              <div className="space-y-4">
                <div className="text-base font-medium text-blue-700 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  📋 Select all that apply:
                </div>
                {question.options.map((option, index) => (
                  <label key={index} className="flex items-start space-x-4 p-4 border-2 border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 cursor-pointer transition-all">
                    <input
                      type="checkbox"
                      checked={userAnswer && userAnswer.includes(index)}
                      onChange={(e) => handleCheckboxChange(question.id, index, e.target.checked)}
                      className="mt-1 text-green-600 rounded scale-125"
                    />
                    <span className="text-gray-900 text-lg flex-1">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {question.type === 'short-answer' && (
              <div className="space-y-4">
                <div className="text-base font-medium text-purple-700 mb-4 bg-purple-50 p-3 rounded-lg border border-purple-200">
                  ✍️ Write your detailed answer below:
                </div>
                <textarea
                  value={userAnswer || ''}
                  onChange={(e) => {
                    console.log('Textarea change event:', e.target.value);
                    handleAnswerChange(question.id, e.target.value);
                  }}
                  onFocus={() => console.log('Textarea focused')}
                  onBlur={() => console.log('Textarea blurred')}
                  onClick={() => console.log('Textarea clicked')}
                  placeholder="Type your answer here... Be specific and include examples where possible."
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-lg"
                  rows={8}
                  style={{ minHeight: '120px' }}
                  autoComplete="off"
                  spellCheck="true"
                />
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  💡 <strong>Tip:</strong> Aim for a detailed explanation with specific examples. Quality over quantity!
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 p-6 border-t border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                currentQuestion === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-300 hover:border-gray-400'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Previous</span>
            </button>

            {/* Progress dots */}
            <div className="flex items-center space-x-2">
              {quiz.questions.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors cursor-pointer ${
                    index === currentQuestion
                      ? 'bg-blue-600'
                      : index < currentQuestion
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                  onClick={() => setCurrentQuestion(index)}
                  title={`Question ${index + 1}`}
                />
              ))}
            </div>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center space-x-2 px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-green-400 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Quiz</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestion(Math.min(quiz.questions.length - 1, currentQuestion + 1))}
                className="flex items-center space-x-2 px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <span>Next</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeQuiz;